import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { Banner, Button, Field, Page, Spinner } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { client, unwrap } from "../../api/client";
import { ApiError } from "../../api/errors";
import { useFeatures } from "../../api/features";
import { useSession } from "../../api/session";
import { t } from "../../i18n/t";
import { safeNext } from "./safeNext";

type LocationState = { from?: string } | null;

/**
 * P1a §7/§11: the multiuser email + password sign-in screen, at `/auth/login`. It sits beside — not instead
 * of — `screens/operator/LoginScreen.tsx`'s one-field operator key form at `/login`: `RequireSession` only
 * sends an anonymous visitor here when the server reports `multiuser_enabled`, and the link back to the
 * operator-key form keeps a single-tenant deployment reachable regardless of which page a bookmark points at.
 */
export function LoginScreen(): JSX.Element {
  const { status, mfa, loginWithPassword } = useSession();
  const { features } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendFailed, setResendFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const from = (location.state as LocationState)?.from ?? "/";
  const next = safeNext(params.get("next"));

  const pendingMfa = mfa === "required" || mfa === "enrollment_required";

  useEffect(() => {
    if (status === "authenticated" && !pendingMfa && next !== null) {
      window.location.assign(next);
    }
  }, [status, pendingMfa, next]);

  if (status === "authenticated" && pendingMfa) {
    return (
      <Navigate to={mfa === "required" ? "/auth/mfa" : "/auth/mfa/setup"} replace state={{ from, next }} />
    );
  }
  if (status === "authenticated") {
    // A `next` target is a real navigation the effect above just kicked off - render nothing (rather than
    // `<Navigate to={from}>`, which would otherwise fire first and yank the browser to the WRONG place).
    return next !== null ? <Spinner /> : <Navigate to={from} replace />;
  }

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    setResent(false);
    setResendFailed(false);
    try {
      const mfa = await loginWithPassword(email, password);
      if (mfa === "required" || mfa === "enrollment_required") {
        // xmg-sec-mfa (S3a): the password alone does not finish the sign-in. The second-factor screen carries
        // `from`/`next` on and lands there once the code is accepted.
        navigate(mfa === "required" ? "/auth/mfa" : "/auth/mfa/setup", {
          replace: true,
          state: { from, next },
        });
      } else if (next !== null) {
        window.location.assign(next);
      } else {
        navigate(from, { replace: true });
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("auth.login.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else if (caught instanceof ApiError && caught.is("email_not_verified")) {
        setUnverified(true);
      } else if (caught instanceof ApiError && caught.is("unauthorized")) {
        setError(t("auth.login.failed"));
      } else {
        // 403 (e.g. csrf_failed), 5xx or a network fault: the password was never judged, so it must not be
        // reported as wrong (xmg-stale-cookie).
        setError(t("auth.login.request_failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onResend(): Promise<void> {
    setBusy(true);
    setResent(false);
    setResendFailed(false);
    try {
      await unwrap(client.POST("/api/v1/auth/verify-email/resend", { body: { email } }));
      setResent(true);
    } catch {
      // Anti-enumeration (P1a §7): the resend endpoint answers 202 whether or not the account exists, so the
      // confirmation is for a 2xx only. A thrown error is a failed request and nothing was queued.
      setResendFailed(true);
    } finally {
      setBusy(false);
    }
  }

  // SignIn.html: the bare form in the right column — "Welcome back" eyebrow, the password field carries
  // the "Forgot password?" link beside its label, the submit is full-width, then the "or" divider and the
  // operator-key link.
  return (
    <AuthLayout>
      <Page narrow eyebrow={t("auth.welcome_back")} title={t("auth.login.title")}>
        {params.get("accepted") === "1" ? (
          <Banner tone="info">{t("auth.login.accepted_flash")}</Banner>
        ) : null}
        {/* CB2 round 2: `lucibook_oauth_callback`'s `session_expired` landing (no `code`, no
            `next=<callback>` - see `connections.py`'s own docstring table) brings the operator here instead
            of the OAuth callback itself; this banner is the one place that outcome is ever shown. */}
        {params.get("error") === "session_expired" ? (
          <Banner tone="warn">{t("oauth.session_expired")}</Banner>
        ) : null}
        <form onSubmit={(event) => void onSubmit(event)} className="authform">
          <Field label={t("auth.login.email")}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="email"
                autoComplete="username"
                placeholder={t("auth.login.email_placeholder")}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Field
            label={t("auth.login.password")}
            error={error}
            labelAside={<Link to="/auth/forgot-password">{t("auth.login.forgot")}</Link>}
          >
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          {unverified ? (
            <Banner tone="warn">
              <p>{t("auth.login.unverified")}</p>
              <Button onClick={() => void onResend()} disabled={busy || email === ""}>
                {t("auth.login.resend")}
              </Button>
              {resent ? <p role="status">{t("auth.login.resend_sent")}</p> : null}
              {resendFailed ? <p role="alert">{t("auth.login.resend_failed")}</p> : null}
            </Banner>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            className="button--auth"
            disabled={busy || email === "" || password === ""}
          >
            {t("auth.login.submit")}
          </Button>
          <div className="authdivider">{t("auth.or_divider")}</div>
          <Link className="button button--auth" to="/login">
            <KeyRound size={16} strokeWidth={2} aria-hidden="true" />
            {t("auth.login.legacy_link")}
          </Link>
          {features.multiuser_enabled ? (
            <p className="authcenter">
              {t("auth.login.register_prompt")}{" "}
              <Link to="/auth/register">{t("auth.login.register_link")}</Link>
            </p>
          ) : null}
        </form>
      </Page>
    </AuthLayout>
  );
}
